import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, TextField, Button } from '@mui/material';

import {
  FormContainer,
  FormHeader,
  FormRow,
  ActionsRow,
  HelperText,
  FeedbackContainer,
} from './EmailMeForm.styles';

const STATIC_FORMS_URL = 'https://api.staticforms.dev/submit';
const API_KEY = process.env.REACT_APP_STATIC_FORMS_API_KEY;
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function EmailMeForm() {
  const [sendersEmail, setSendersEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [botcheck, setBotcheck] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [captchaReady, setCaptchaReady] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [hasSent, setHasSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [messageError, setMessageError] = useState('');
  const recaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const messageFieldRef = useRef(null);
  const emailFieldRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const initializeRecaptcha = () => {
      if (!window.grecaptcha || !recaptchaRef.current) {
        return;
      }

      window.grecaptcha.ready(() => {
        if (widgetIdRef.current !== null) {
          window.grecaptcha.reset(widgetIdRef.current);
        } else {
          widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: SITE_KEY || '',
            callback: token => {
              setRecaptchaToken(token);
              setFeedback(null);
            },
            'expired-callback': () => {
              setRecaptchaToken('');
            },
            'error-callback': () => {
              setRecaptchaToken('');
              setFeedback({
                type: 'error',
                message:
                  'reCAPTCHA had trouble loading. Please refresh and try again.',
              });
            },
          });
          // Add aria-label to reCAPTCHA's hidden textarea after it's created
          setTimeout(() => {
            // Handle both base ID and numbered variants (e.g., g-recaptcha-response-1)
            const recaptchaTextareas = document.querySelectorAll(
              'textarea[id^="g-recaptcha-response"]'
            );
            recaptchaTextareas.forEach(textarea => {
              textarea.setAttribute('aria-label', 'reCAPTCHA response');
              textarea.setAttribute('aria-hidden', 'true');
            });
          }, 100);
        }
        setCaptchaReady(true);
        // Add aria-label to reCAPTCHA's hidden textarea after initialization
        setTimeout(() => {
          // Handle both base ID and numbered variants (e.g., g-recaptcha-response-1)
          const recaptchaTextareas = document.querySelectorAll(
            'textarea[id^="g-recaptcha-response"]'
          );
          recaptchaTextareas.forEach(textarea => {
            textarea.setAttribute('aria-label', 'reCAPTCHA response');
            textarea.setAttribute('aria-hidden', 'true');
          });
        }, 100);
      });
    };

    const existingScript = document.getElementById('google-recaptcha-script');

    if (existingScript) {
      if (window.grecaptcha) {
        initializeRecaptcha();
      } else {
        existingScript.addEventListener('load', initializeRecaptcha);
      }
      return () => {
        existingScript.removeEventListener('load', initializeRecaptcha);
      };
    }

    const script = document.createElement('script');
    script.id = 'google-recaptcha-script';
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = initializeRecaptcha;
    script.onerror = () => {
      setFeedback({
        type: 'error',
        message:
          'Unable to load reCAPTCHA. Please check your connection and reload the page.',
      });
      setCaptchaReady(true); // Stop showing loading message
    };

    document.body.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []);

  // Label all reCAPTCHA textareas (including dynamically created ones)
  useEffect(() => {
    const labelRecaptchaTextareas = () => {
      const recaptchaTextareas = document.querySelectorAll(
        'textarea[id^="g-recaptcha-response"]'
      );
      recaptchaTextareas.forEach(textarea => {
        if (!textarea.getAttribute('aria-label')) {
          textarea.setAttribute('aria-label', 'reCAPTCHA response');
          textarea.setAttribute('aria-hidden', 'true');
        }
      });
    };

    // Label immediately and periodically check for new ones
    labelRecaptchaTextareas();
    const intervalId = setInterval(labelRecaptchaTextareas, 500);

    // Also watch for DOM changes
    const observer = new MutationObserver(labelRecaptchaTextareas);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearInterval(intervalId);
      observer.disconnect();
    };
  }, []);

  // Label Material-UI's hidden textarea used for multiline height calculation
  useEffect(() => {
    const labelHiddenTextarea = () => {
      if (messageFieldRef.current) {
        const hiddenTextarea = messageFieldRef.current.querySelector(
          'textarea[aria-hidden="true"][readonly]'
        );
        if (hiddenTextarea && !hiddenTextarea.getAttribute('aria-label')) {
          hiddenTextarea.setAttribute(
            'aria-label',
            'Message field height calculation'
          );
        }
      }
    };

    // Try to label it immediately and also after a delay
    labelHiddenTextarea();
    const timeoutId = setTimeout(labelHiddenTextarea, 100);
    const observer = new MutationObserver(labelHiddenTextarea);

    if (messageFieldRef.current) {
      observer.observe(messageFieldRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [message]);

  // Focus email field when hash is present in URL
  useEffect(() => {
    if (location.hash === '#focus-form' && emailFieldRef.current) {
      // Remove hash from URL first using React Router to maintain state consistency
      navigate(location.pathname + location.search, { replace: true });

      // Focus after navigation completes and after any other focus management
      // Use a longer delay to ensure it happens after PageTracker's focus management
      setTimeout(() => {
        const input = emailFieldRef.current.querySelector('input');
        if (input) {
          input.focus();
        }
      }, 300);
    }
  }, [location.hash, navigate, location.pathname, location.search]);

  const validateFields = () => {
    let isValid = true;
    setEmailError('');
    setSubjectError('');
    setMessageError('');

    // Validate email
    if (!sendersEmail.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(sendersEmail)) {
        setEmailError('Please enter a valid email address');
        isValid = false;
      }
    }

    // Validate subject
    if (!subject.trim()) {
      setSubjectError('Subject is required');
      isValid = false;
    }

    // Validate message
    if (!message.trim()) {
      setMessageError('Message is required');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    if (botcheck) {
      return;
    }

    // Validate fields
    if (!validateFields()) {
      return;
    }

    if (!recaptchaToken) {
      setFeedback({
        type: 'error',
        message: 'Please complete the reCAPTCHA challenge before submitting.',
      });
      return;
    }

    if (!API_KEY) {
      setFeedback({
        type: 'error',
        message:
          'Form configuration error. Please contact the site administrator.',
      });
      return;
    }

    setSubmitting(true);
    setFeedback(null);

    const payload = {
      apiKey: API_KEY,
      subject,
      email: sendersEmail,
      message,
      replyTo: sendersEmail,
      botcheck,
      'g-recaptcha-response': recaptchaToken,
    };

    fetch(STATIC_FORMS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async response => {
        const data = await response.json();
        if (response.ok) {
          setHasSent(true);
          setFeedback(null);
        } else {
          setFeedback({
            type: 'error',
            message:
              data?.message ||
              'Hmm, something went wrong while sending your message. Please try again.',
          });
        }
      })
      .catch(error => {
        setFeedback({
          type: 'error',
          message:
            error.message || 'Network hiccup! Please try again in a moment.',
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleClear = () => {
    setSendersEmail('');
    setSubject('');
    setMessage('');
    setBotcheck('');
    setRecaptchaToken('');
    setFeedback(null);
    setHasSent(false);
    setEmailError('');
    setSubjectError('');
    setMessageError('');
    if (
      typeof window !== 'undefined' &&
      window.grecaptcha &&
      widgetIdRef.current !== null
    ) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  };

  const emailErrorId = 'email-error';
  const subjectErrorId = 'subject-error';
  const messageErrorId = 'message-error';
  const feedbackId = 'form-feedback';

  return (
    <FormContainer
      component="form"
      onSubmit={handleSubmit}
      spacing={2}
      noValidate
    >
      <FormHeader component="h2">Let&apos;s connect</FormHeader>
      <FeedbackContainer
        id={feedbackId}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        hasFeedback={!!feedback}
      >
        {feedback && (
          <Alert severity={feedback.type === 'error' ? 'error' : feedback.type}>
            {feedback.message}
          </Alert>
        )}
      </FeedbackContainer>
      <FormRow ref={emailFieldRef}>
        <TextField
          label="Your Email"
          type="email"
          value={sendersEmail}
          onChange={event => {
            setSendersEmail(event.target.value);
            if (emailError) {
              setEmailError('');
            }
          }}
          required
          fullWidth
          aria-describedby={emailErrorId}
          error={!!emailError}
          inputProps={{
            'aria-invalid': !!emailError,
          }}
        />
        <span id={emailErrorId} className="sr-only" aria-live="polite">
          {emailError}
        </span>
      </FormRow>
      <FormRow>
        <TextField
          label="Subject"
          value={subject}
          onChange={event => {
            setSubject(event.target.value);
            if (subjectError) {
              setSubjectError('');
            }
          }}
          required
          fullWidth
          aria-describedby={subjectErrorId}
          error={!!subjectError}
          inputProps={{
            'aria-invalid': !!subjectError,
          }}
        />
        <span id={subjectErrorId} className="sr-only" aria-live="polite">
          {subjectError}
        </span>
      </FormRow>
      <FormRow ref={messageFieldRef}>
        <TextField
          label="Message"
          value={message}
          onChange={event => {
            setMessage(event.target.value);
            if (messageError) {
              setMessageError('');
            }
          }}
          required
          fullWidth
          multiline
          minRows={4}
          aria-describedby={messageErrorId}
          error={!!messageError}
          inputProps={{
            'aria-invalid': !!messageError,
            'aria-label': 'Message',
          }}
          InputProps={{
            'aria-label': 'Message',
          }}
        />
        <span id={messageErrorId} className="sr-only" aria-live="polite">
          {messageError}
        </span>
      </FormRow>
      <label htmlFor="botcheck" className="sr-only">
        Leave this field empty (bot check)
      </label>
      <input
        type="text"
        id="botcheck"
        name="botcheck"
        value={botcheck}
        onChange={event => setBotcheck(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
        style={{ display: 'none' }}
        aria-hidden="true"
        aria-label="Leave this field empty (bot check)"
      />
      <FormRow>
        <div
          ref={recaptchaRef}
          role="group"
          aria-label="reCAPTCHA verification"
          aria-describedby="recaptcha-description"
        />
        <span id="recaptcha-description" className="sr-only">
          Please complete the reCAPTCHA verification to submit the form
        </span>
      </FormRow>
      {!captchaReady && (
        <HelperText id="recaptcha-loading" aria-live="polite">
          Loading reCAPTCHA challenge…
        </HelperText>
      )}
      {captchaReady && (
        <HelperText id="recaptcha-ready" aria-live="polite">
          Send me a note and I will get back to you soon.
        </HelperText>
      )}
      <ActionsRow>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={submitting || hasSent}
        >
          {submitting ? 'Sending…' : hasSent ? 'Sent' : 'Send Message'}
        </Button>
        {hasSent && (
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={handleClear}
          >
            Clear Form
          </Button>
        )}
      </ActionsRow>
    </FormContainer>
  );
}

export default EmailMeForm;
